import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Trash2, Star, Image as ImageIcon, Plus, Edit2 } from 'lucide-react';
import { fetchCategories as loadCategories } from '../services/sellerCatalog';
import api from '../services/api';
import { useSellerLiveInvalidation, useSellerProducts } from '../hooks/queries/sellerQueries';
import { isVariableProductType } from '../utils/productVariants';

interface ProductVariant {
  id?: string;
  _id?: string;
  sku?: string;
  size?: string;
  color?: string;
  price?: number;
  quantityAvailable?: number;
  stock?: number;
  attributes?: Array<{ name: string; value: string }>;
}

interface Product {
  id?: string;
  _id?: string;
  name: string;
  description?: string;
  brand: string;
  price?: number;
  originalPrice?: number;
  productType?: 'simple' | 'variable' | 'variant';
  color?: string;
  availableSizes?: string[];
  availableColors?: string[];
  variantConfig?: {
    hasSizes?: boolean;
    hasColors?: boolean;
    sizes?: string[];
    colors?: string[];
  };
  category?: {
    id?: string;
    _id?: string;
    name: string;
  } | null;
  isActive: boolean;
  isFlash?: boolean;
  isRecommended?: boolean;
  rating?: number;
  noOfReviews?: number;
  quantityAvailable?: number;
  totalStock?: number;
  images?: string[];
  variants?: ProductVariant[];
  createdAt?: string;
}

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['products', 'common']);
  const { invalidateAll } = useSellerLiveInvalidation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);

  const { data: productsData, isLoading, refetch } = useSellerProducts({
    page: currentPage,
    limit: 12,
    search: searchTerm || undefined,
    isActive: filterActive || undefined,
    category: filterCategory || undefined,
  });

  const products = ((productsData?.products || []) as Product[]).map((product) => ({
    ...product,
    id: product.id || product._id?.toString(),
    rating: product.rating ?? 0,
    noOfReviews: product.noOfReviews ?? 0,
  }));
  const total = productsData?.pagination?.total ?? 0;
  const totalPages = productsData?.pagination?.totalPages ?? 1;

  useEffect(() => {
    loadCategories<{ id: string; name: string }>()
      .then(setCategories)
      .catch((error) => console.error('Error fetching categories:', error));
  }, []);

  const handleToggleStatus = async (productId: string | undefined, isActive: boolean) => {
    if (!productId) return;
    try {
      await api.put(`/seller/products/${productId}`, { isActive: !isActive });
      await refetch();
    } catch (error) {
      console.error('Error updating product status:', error);
    }
  };

  const handleDeleteProduct = async (productId: string | undefined, productName: string) => {
    if (!productId) return;
    if (window.confirm(t('messages.confirmDelete', { name: productName }))) {
      try {
        await api.delete(`/seller/products/${productId}`);
        await invalidateAll();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const formatPrice = (price?: number) => (price != null ? `$${price.toFixed(2)}` : 'N/A');

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDisplayStock = (product: Product) => {
    const variantStock = product.variants?.reduce(
      (sum, v) => sum + (v.quantityAvailable ?? v.stock ?? 0),
      0
    );
    return (
      product.totalStock ??
      (variantStock && variantStock > 0 ? variantStock : undefined) ??
      product.quantityAvailable
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-600 mt-2">{t('subtitle')}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            {t('table.totalResults', { ns: 'common' })}: {total}{' '}
            {t('title').toLowerCase()}
          </div>
          <button
            type="button"
            onClick={() => navigate('/products/add')}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus size={20} />
            <span>{t('addProduct')}</span>
          </button>
        </div>
      </div>

      <div className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('search')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="input-field pl-10"
            />
          </div>

          <select
            value={filterActive}
            onChange={(e) => {
              setFilterActive(e.target.value);
              setCurrentPage(1);
            }}
            className="input-field"
          >
            <option value="">{t('filters.allStatus')}</option>
            <option value="true">{t('filters.active')}</option>
            <option value="false">{t('filters.inactive')}</option>
          </select>

          <select
            value={filterCategory}
            onChange={(e) => {
              setFilterCategory(e.target.value);
              setCurrentPage(1);
            }}
            className="input-field"
          >
            <option value="">{t('filters.allCategories')}</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>

          <button
            type="button"
            onClick={() => {
              setSearchTerm('');
              setFilterActive('');
              setFilterCategory('');
              setCurrentPage(1);
            }}
            className="btn-outline"
          >
            {t('buttons.clearFilters', { ns: 'common' })}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {isLoading && !productsData ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-custom-primary" />
          </div>
        ) : products.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            {searchTerm || filterActive || filterCategory
              ? t('messages.noProductsMatchingFilters')
              : t('messages.noProductsFound')}
          </div>
        ) : (
          products.map((product, index) => {
            const productId = product.id || product._id;
            const displayStock = getDisplayStock(product);
            return (
              <div key={productId || `product-${index}`} className="card hover:shadow-card-hover transition-shadow">
                <div className="relative h-48 bg-gray-100 rounded-t-lg">
                  {product.images && product.images.length > 0 ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-t-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div
                    className={`absolute inset-0 flex items-center justify-center bg-gray-100 rounded-t-lg ${
                      product.images && product.images.length > 0 ? 'hidden' : ''
                    }`}
                  >
                    <ImageIcon size={48} className="text-gray-400" />
                  </div>

                  <div className="absolute top-2 left-2 flex flex-col space-y-1">
                    <span className={`badge-${product.isActive ? 'green' : 'red'} text-xs`}>
                      {product.isActive
                        ? t('status.active', { ns: 'common' })
                        : t('status.inactive', { ns: 'common' })}
                    </span>
                    {product.isRecommended && (
                      <span className="badge-yellow text-xs">{t('badges.recommended')}</span>
                    )}
                    {product.isFlash && (
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                        {t('badges.flash')}
                      </span>
                    )}
                    {isVariableProductType(product.productType) && (
                      <span className="badge-blue text-xs">{t('badges.variable')}</span>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  <div className="mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 mb-1">
                      {product.name}
                    </h3>
                    <p className="text-xs text-gray-500 mb-2">{product.brand}</p>
                  </div>

                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {product.price != null && (
                        <span className="font-bold text-primary-600">{formatPrice(product.price)}</span>
                      )}
                      {product.originalPrice != null &&
                        product.originalPrice > (product.price || 0) && (
                          <span className="text-xs text-gray-500 line-through">
                            {formatPrice(product.originalPrice)}
                          </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1">
                      <Star size={12} className="text-yellow-400 fill-current" />
                      <span className="text-xs text-gray-600">
                        {(product.rating ?? 0).toFixed(1)} ({product.noOfReviews ?? 0})
                      </span>
                    </div>
                  </div>

                  <div className="text-xs text-gray-500 mb-3">
                    {product.category?.name && (
                      <div>
                        {t('fields.category')}: {product.category.name}
                      </div>
                    )}
                    {displayStock !== undefined && (
                      <div>
                        {t('fields.stock')}: {displayStock}
                      </div>
                    )}
                    {(() => {
                      const sizes =
                        product.availableSizes?.length
                          ? product.availableSizes
                          : product.variantConfig?.sizes?.length
                            ? product.variantConfig.sizes
                            : Array.from(
                                new Set(
                                  (product.variants || [])
                                    .map((v) => v.size)
                                    .filter(Boolean) as string[]
                                )
                              );
                      const colors =
                        product.availableColors?.length
                          ? [...product.availableColors]
                          : product.variantConfig?.colors?.length
                            ? [...product.variantConfig.colors]
                            : Array.from(
                                new Set(
                                  (product.variants || [])
                                    .map((v) => v.color)
                                    .filter(Boolean) as string[]
                                )
                              );
                      const displayColors =
                        product.color && colors.length === 0 ? [product.color] : colors;
                      return (
                        <>
                          {sizes.length > 0 && (
                            <div>
                              {t('fields.sizes')}: {sizes.join(', ')}
                            </div>
                          )}
                          {displayColors.length > 0 && (
                            <div>
                              {t('fields.colors')}: {displayColors.join(', ')}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {product.variants && product.variants.length > 0 && (
                      <div className="mt-2">
                        <div className="font-semibold text-gray-700 mb-1">SKUs:</div>
                        {product.variants.slice(0, 3).map((variant) => (
                          <div key={variant.id || variant._id || variant.sku} className="text-xs">
                            <span className="font-mono text-blue-600">{variant.sku || '—'}</span>
                            {(variant.size || variant.color) && (
                              <span className="text-gray-500 ml-1">
                                ({[variant.size, variant.color].filter(Boolean).join(', ')})
                              </span>
                            )}
                          </div>
                        ))}
                        {product.variants.length > 3 && (
                          <div className="text-xs text-gray-400">
                            +{product.variants.length - 3} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(productId, product.isActive)}
                      className={`btn-${product.isActive ? 'outline' : 'primary'} text-xs py-1 px-2`}
                    >
                      {product.isActive
                        ? t('buttons.deactivate', { ns: 'common' })
                        : t('buttons.activate', { ns: 'common' })}
                    </button>

                    <div className="flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => navigate(`/products/edit/${productId}`)}
                        className="p-1 text-gray-400 hover:text-custom-primary"
                        title={t('editProduct')}
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteProduct(productId, product.name)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title={t('actions.deleteProduct')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {product.createdAt && (
                    <div className="text-xs text-gray-400 mt-2">
                      {t('messages.added')} {formatDate(product.createdAt)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && (
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-500">
              {t('table.showing', { ns: 'common' })} {(currentPage - 1) * 12 + 1}{' '}
              {t('table.to', { ns: 'common' })} {Math.min(currentPage * 12, total)}{' '}
              {t('table.of', { ns: 'common' })} {total} {t('table.results', { ns: 'common' })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.previous', { ns: 'common' })}
              </button>
              <span className="text-sm text-gray-700">
                {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })}{' '}
                {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
              >
                {t('table.next', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
